import { formatInTimeZone } from 'date-fns-tz';
import { CaseLinkType } from '@/api/apis/case';
import { TMDXFile } from '@/api/apis/files';
import { dateTimeToISO } from '@/utils/helpers/date.helpers';
import { getLocationAddress } from '@/utils/helpers/case.helpers';
import { Uuid } from '@/api/types';
import {
  CreateOfferRequest,
  GetOffersOrderBy,
  Offer,
  OfferMatchDetails,
  OfferNote,
  OfferNoteResponse,
  OfferRecipientDetails,
  OfferServiceAssignedCustomer,
  OfferServiceCreateBody,
  OfferServiceFileModel,
  OfferServiceHospitalLocation,
  OfferServiceLinkModel,
  OfferServiceListQuery,
  OfferServiceMatchDetails,
  OfferServiceOpoLocation,
  OfferServiceRecipientDetails,
  OfferServiceResponse,
  OfferOpoContact,
  OfferServiceWriteBody,
  OffersListFilters,
  OfferStatus,
  SortDirection,
  UpdateOfferRequest,
} from './offer.types';

const ASSIGNEE_GROUP = {
  receivingSurgeon: 'CustomerReceivingSurgeons',
  coordinator: 'CustomerCoordinators',
  offerReviewer: 'CustomerOfferCoordinators',
} as const;

const toAddress = (location?: OfferServiceHospitalLocation | OfferServiceOpoLocation): string =>
  getLocationAddress({
    country: location?.country ?? '',
    city: location?.city ?? '',
    postalCode: location?.postalCode ?? '',
    addressLine: location?.addressLine ?? '',
    state: location?.state ?? null,
  });

export const mapOfferMatchToServiceModel = (match: OfferMatchDetails): OfferServiceMatchDetails => ({
  offerType: match.offerType,
  matchId: match.matchId,
  matchSeq: match.matchSeq,
  matchRank: match.matchRank,
});

export const mapOfferServiceMatchToOfferMatch = (match: OfferServiceMatchDetails): OfferMatchDetails => ({
  offerType: match.offerType,
  matchId: match.matchId ?? '',
  matchSeq: match.matchSeq,
  matchRank: match.matchRank ?? undefined,
});

export const mapOfferServiceRecipientToOfferRecipient = (
  recipient: OfferServiceRecipientDetails,
): OfferRecipientDetails => ({
  name: recipient.name ?? undefined,
  medicalRecordNumber: recipient.medicalRecordNumber ?? undefined,
  dateOfBirth: recipient.dateOfBirth ?? undefined,
  age: recipient.age ?? undefined,
  heightCm: recipient.heightCm ?? undefined,
  weightG: recipient.weightG ?? undefined,
  bmi: recipient.bmi ?? undefined,
  gender: recipient.gender,
  bloodType: recipient.bloodType,
  priorTransplant: recipient.priorTransplant ?? undefined,
  multivisc: recipient.multivisc ?? undefined,
  kidneyInfo: recipient.kidneyInfo
    ? {
        cpra: recipient.kidneyInfo.cpra ?? undefined,
        waitTime: recipient.kidneyInfo.waitTime ?? undefined,
        dialysisTime: recipient.kidneyInfo.dialysisTime ?? undefined,
        diabetic: recipient.kidneyInfo.diabetic ?? undefined,
        epts: recipient.kidneyInfo.epts ?? undefined,
      }
    : undefined,
  heartInfo: recipient.heartInfo
    ? {
        lvad: recipient.heartInfo.lvad ?? undefined,
        phm: recipient.heartInfo.phm ?? undefined,
        prv: recipient.heartInfo.prv ?? undefined,
      }
    : undefined,
  liverInfo: recipient.liverInfo
    ? {
        meldPeld: recipient.liverInfo.meldPeld ?? undefined,
        isSlkPatient: recipient.liverInfo.isSlkPatient ?? undefined,
      }
    : undefined,
});

export const mapOfferRecipientToServiceModel = (recipient: OfferRecipientDetails): OfferServiceRecipientDetails => ({
  name: recipient.name || undefined,
  medicalRecordNumber: recipient.medicalRecordNumber || undefined,
  dateOfBirth: recipient.dateOfBirth || undefined,
  age: recipient.age,
  heightCm: recipient.heightCm,
  weightG: recipient.weightG,
  bmi: recipient.bmi,
  gender: recipient.gender,
  bloodType: recipient.bloodType,
  priorTransplant: recipient.priorTransplant || undefined,
  multivisc: recipient.multivisc || undefined,
  kidneyInfo: recipient.kidneyInfo,
  heartInfo: recipient.heartInfo,
  liverInfo: recipient.liverInfo,
});

// PUT is a full replace: callers must pass the complete entity (current offer + edits), not a partial.
export const mapOfferToServiceWriteBody = (body: CreateOfferRequest | UpdateOfferRequest): OfferServiceWriteBody => {
  const defaultTxAssignees: OfferServiceAssignedCustomer[] = [];
  if (body.receivingSurgeonId) {
    defaultTxAssignees.push({ customerId: body.receivingSurgeonId, group: ASSIGNEE_GROUP.receivingSurgeon });
  }
  if (body.coordinatorId && !body.isServiceWithoutCoordinator) {
    defaultTxAssignees.push({ customerId: body.coordinatorId, group: ASSIGNEE_GROUP.coordinator });
  }
  if (body.primaryOfferReviewerId) {
    defaultTxAssignees.push({ customerId: body.primaryOfferReviewerId, group: ASSIGNEE_GROUP.offerReviewer });
  }

  const opoContacts: Uuid[] = [];
  if (body.opoContactId) opoContacts.push(body.opoContactId);
  if (body.secondaryOpoContactId) opoContacts.push(body.secondaryOpoContactId);

  const donorOr =
    body.donorHospitalDate && body.donorHospitalTime && body.donorHospitalTimeZone
      ? {
          dateTime: dateTimeToISO(body.donorHospitalDate, body.donorHospitalTime, body.donorHospitalTimeZone),
          timezone: body.donorHospitalTimeZone,
        }
      : undefined;

  return {
    donorId: body.donorId,
    organType: body.organType,
    donorType: body.donorType,
    match: mapOfferMatchToServiceModel(body.match),
    recipient: body.recipient ? mapOfferRecipientToServiceModel(body.recipient) : undefined,
    isInpatient: body.isInpatient,
    laterality: body.laterality,
    matchingSizeCriteria: body.matchingSizeCriteria || undefined,
    isXMatchNeeded: body.isXMatchNeeded,
    xMatchType: body.xMatchType || undefined,
    diagnosis: body.diagnosis || undefined,
    defaultTxAssignees,
    // TODO: uncomment once BE supports `isServiceWithoutCoordinator` on the offer write body.
    // isServiceWithoutCoordinator: !!body.isServiceWithoutCoordinator,
    donorHospitalId: body.donorHospitalId || undefined,
    opoId: body.opoId || undefined,
    opoContacts,
    donorOr,
  };
};

export const mapOfferLinkToServiceModel = (link: CaseLinkType): OfferServiceLinkModel => ({
  url: link.url,
  description: link.description || undefined,
});

// Maps an already-uploaded file (id + path resolved by the signed-url flow) to the BE file model.
export const mapUploadedFileToServiceModel = (file: TMDXFile): OfferServiceFileModel => ({
  id: file.id!,
  fileName: file.fileName,
  path: file.path,
});

export const mapOfferToServiceCreateBody = (body: CreateOfferRequest): OfferServiceCreateBody => ({
  ...mapOfferToServiceWriteBody(body),
  files: (body.files ?? []).map(mapUploadedFileToServiceModel),
  links: (body.links ?? []).map(mapOfferLinkToServiceModel),
});

// useFilterQuery.transformFilters joins non-empty arrays to CSV strings, but still forwards
// empty arrays as `[]`. Accept both so list mapping never throws on `.trim()`.
const toQueryStringArray = (value?: string | string[]): string[] | undefined => {
  if (value == null || value === '') return undefined;

  const items = (Array.isArray(value) ? value : value.split(',')).map((item) => String(item).trim()).filter(Boolean);

  return items.length ? items : undefined;
};

// Maps OffersPage/columns.ts sort keys to the BE's GetOffersOrderBy enum members.
const OFFERS_ORDER_BY_MAP: Record<string, GetOffersOrderBy> = {
  createdAt: 'CreatedAt',
  matchSeq: 'MatchSeq',
  matchRank: 'MatchRank',
  recipientName: 'RecipientName',
};

export const mapOffersListFiltersToServiceQuery = (filters: OffersListFilters): OfferServiceListQuery => {
  const donorId = filters.searchQuery?.trim() || undefined;
  const orderBy = filters.orderBy ? OFFERS_ORDER_BY_MAP[filters.orderBy] : undefined;
  const sortDirection: SortDirection = filters.asc ? 'Ascending' : 'Descending';

  return {
    pageIndex: filters.pageIndex,
    itemsPerPage: filters.itemsPerPage,
    donorId,
    statuses: toQueryStringArray(filters.statuses),
    opoIds: toQueryStringArray(filters.opoIds),
    organTypes: toQueryStringArray(filters.organTypes),
    createdDateFrom: filters.createdDateFrom,
    createdDateTo: filters.createdDateTo,
    orderBy,
    sortDirection: orderBy ? sortDirection : undefined,
  };
};

export const mapOfferNoteResponseToOfferNote = (note: OfferNoteResponse): OfferNote => ({
  id: note.id,
  message: note.message ?? undefined,
  authorUserId: note.authorUserId,
  author: {
    userId: note.author.userId ?? undefined,
    firstName: note.author.firstName ?? undefined,
    lastName: note.author.lastName ?? undefined,
    photoPath: note.author.photoPath ?? undefined,
    email: note.author.email ?? undefined,
    primaryPhone: note.author.phone ?? undefined,
    secondaryPhone: note.author.secondaryPhone ?? undefined,
    enabled: note.author.enabled ?? undefined,
    customerId: note.author.customerId,
    userTypes: note.author.userTypes,
    roles: note.author.roles,
  },
  createdAt: note.createdAt,
  modifiedAt: note.modifiedAt ?? undefined,
  isPinned: note.isPinned,
});

export const mapOfferServiceResponseToOffer = (res: OfferServiceResponse): Offer => {
  const surgeon = res.defaultTxAssignees?.find((a) => a.group === ASSIGNEE_GROUP.receivingSurgeon);
  const coordinator = res.defaultTxAssignees?.find((a) => a.group === ASSIGNEE_GROUP.coordinator);
  const offerReviewer = res.defaultTxAssignees?.find((a) => a.group === ASSIGNEE_GROUP.offerReviewer);

  const donorOr = res.donorOrDateTime;
  const donorOrTz = donorOr?.timeZoneId ?? undefined;
  const donorOrDate = donorOr ? new Date(donorOr.dateTime) : undefined;
  const donorHospitalDate = donorOrDate ? formatInTimeZone(donorOrDate, donorOrTz ?? 'UTC', 'yyyy-MM-dd') : undefined;
  const donorHospitalTime = donorOrDate ? formatInTimeZone(donorOrDate, donorOrTz ?? 'UTC', 'HH:mm') : undefined;

  const donorId = res.donorId ?? '';

  const txRepresentatives = (res.defaultTxAssignees ?? []).map((a) => ({
    customerId: a.customerId ?? undefined,
    userId: a.userId ?? undefined,
    firstName: a.firstName ?? undefined,
    lastName: a.lastName ?? undefined,
    email: a.email ?? undefined,
    primaryPhone: a.primaryPhone ?? undefined,
    group: a.group ?? undefined,
    photoPath: a.photoPath ?? undefined,
  }));

  // Order matters: first contact is the primary (source), second is the secondary.
  const opoContacts: OfferOpoContact[] = (res.opoContacts ?? []).map((c) => ({
    id: c.id,
    userId: c.userId ?? undefined,
    firstName: c.firstName ?? undefined,
    lastName: c.lastName ?? undefined,
    email: c.email ?? undefined,
    primaryPhone: c.primaryPhone ?? undefined,
    secondaryPhone: c.secondaryPhone ?? undefined,
    photoPath: c.photoPath ?? undefined,
  }));
  const [primaryOpoContact, secondaryOpoContact] = opoContacts;

  const files: TMDXFile[] = (res.files ?? []).map((f) => ({
    id: f.id,
    fileName: f.fileName ?? '',
    path: f.path ?? '',
    signedUrl: f.signedUrl ?? undefined,
  }));

  const links: CaseLinkType[] = (res.links ?? []).map((l) => ({
    id: l.id,
    url: l.url ?? '',
    description: l.description ?? '',
  }));

  const notes: OfferNote[] = (res.notes ?? []).map(mapOfferNoteResponseToOfferNote);

  return {
    offerId: res.id,
    txCenterId: res.txCenterId ?? '',
    status: res.status ?? OfferStatus.Presented,
    unosId: donorId,
    donorId,
    organType: res.organType,
    donorType: res.donorType,
    match: res.match ? mapOfferServiceMatchToOfferMatch(res.match) : undefined,
    recipient: res.recipient ? mapOfferServiceRecipientToOfferRecipient(res.recipient) : undefined,
    isInpatient: res.isInpatient ?? null,
    laterality: res.laterality,
    matchingSizeCriteria: res.matchingSizeCriteria ?? undefined,
    isXMatchNeeded: res.isXMatchNeeded ?? null,
    xMatchType: res.xMatchType ?? undefined,
    diagnosis: res.diagnosis ?? undefined,
    receivingSurgeonId: surgeon?.customerId ?? undefined,
    coordinatorId: coordinator?.customerId ?? undefined,
    primaryOfferReviewerId: offerReviewer?.customerId ?? undefined,
    isServiceWithoutCoordinator: res.isServiceWithoutCoordinator ?? null,
    donorHospitalId: res.donorHospitalDetails?.id,
    donorHospitalName: res.donorHospitalDetails?.name ?? undefined,
    donorHospitalAddress: res.donorHospitalDetails ? toAddress(res.donorHospitalDetails.location) : undefined,
    donorHospitalLogoPath: res.donorHospitalDetails?.logoPath ?? undefined,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone: donorOrTz,
    opoId: res.opoDetails?.id,
    opoName: res.opoDetails?.name ?? undefined,
    opoContacts,
    opoContactId: primaryOpoContact?.id,
    opoSourceContactPhone: primaryOpoContact?.primaryPhone,
    secondaryOpoContactId: secondaryOpoContact?.id,
    secondaryOpoContactPhone: secondaryOpoContact?.primaryPhone,
    txRepresentatives,
    files,
    links,
    notes,
    createdAt: res.createdAt ?? '',
    updatedAt: res.updatedAt ?? '',
    declineReason: res.declineReason ?? undefined,
    offerAndDonorDetailsText: res.offerAndDonorDetailsText ?? undefined,
  };
};
